import { RefObject, useEffect } from 'react';
import { pointerEngine } from './pointerEngine';
import { playLoop } from './playLoop';
import { PRESETS, stepSpring, settled } from './springs';
import { RectCache, nearRect } from './rectCache';
import { isProtectedElement } from './protect';
import { useStorm } from '../storm/StormContext';
import { useFpsBackoff } from '../storm/useFpsBackoff';

// Magnetic button: pulled up to PULL px toward the pointer when it is within RANGE px of the
// button (mouse hover; touch while pressed nearby) and squashed to PRESS_SCALE while pressed.
// Disabled buttons are inert. Reduced motion: press squash only. Backoff step ≥2: fully off.
const PULL = 6;
const RANGE = 60;
const PRESS_SCALE = 0.96;
const EPS = 0.05;

type S = { x: number; v: number };

function pullFor(r: DOMRect, px: number, py: number): { tx: number; ty: number } {
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  const ex = Math.max(r.left - px, 0, px - r.right), ey = Math.max(r.top - py, 0, py - r.bottom);
  const edge = Math.hypot(ex, ey);
  if (edge >= RANGE) return { tx: 0, ty: 0 };
  const dx = px - cx, dy = py - cy;
  const d = Math.hypot(dx, dy);
  if (d === 0) return { tx: 0, ty: 0 };
  const reach = Math.min(1, d / (Math.max(r.width, r.height) / 2)); // centred pointer → no pull
  const k = PULL * reach * (1 - edge / RANGE);
  return { tx: (dx / d) * k, ty: (dy / d) * k };
}

export function useMagneticButton(ref: RefObject<HTMLButtonElement>): void {
  const { reducedMotion } = useStorm();
  const q = useFpsBackoff();
  const pressOnly = reducedMotion;
  const off = q >= 2;

  useEffect(() => {
    const el = ref.current;
    if (!el || off || isProtectedElement(el)) return;
    const box = new RectCache(() => ref.current);
    let x: S = { x: 0, v: 0 }, y: S = { x: 0, v: 0 }, s: S = { x: 1, v: 0 };
    let wx = 0, wy = 0, ws = 1;
    let unreg: (() => void) | null = null;
    let moved = false;

    const targets = () => {
      const p = pointerEngine.state;
      const r = box.get(performance.now(), moved);
      if (!r || el.disabled) return { tx: 0, ty: 0, ts: 1 };
      const live = p.type === 'mouse' || p.down;
      const near = live && nearRect(r, p.x, p.y, RANGE);
      const pressed = p.down && !!p.target && el.contains(p.target);
      const pull = near && !pressOnly ? pullFor(r, p.x, p.y) : { tx: 0, ty: 0 };
      return { tx: pull.tx, ty: pull.ty, ts: pressed ? PRESS_SCALE : 1 };
    };
    const write = () => {
      if (Math.abs(x.x - wx) < EPS && Math.abs(y.x - wy) < EPS && Math.abs(s.x - ws) < 0.001) return;
      wx = x.x; wy = y.x; ws = s.x;
      const rest = Math.abs(wx) < EPS && Math.abs(wy) < EPS && Math.abs(ws - 1) < 0.001;
      el.style.transform = rest ? '' : `translate3d(${wx.toFixed(2)}px,${wy.toFixed(2)}px,0) scale(${ws.toFixed(4)})`;
    };
    const tick = (dt: number) => {
      const t = targets();
      moved = false;
      x = stepSpring(x, t.tx, dt, PRESETS.snappy);
      y = stepSpring(y, t.ty, dt, PRESETS.snappy);
      s = stepSpring(s, t.ts, dt, PRESETS.snappy);
      const done = settled(x, t.tx) && settled(y, t.ty) && settled(s, t.ts, 0.0005);
      if (done) { x = { x: t.tx, v: 0 }; y = { x: t.ty, v: 0 }; s = { x: t.ts, v: 0 }; }
      write();
      if (done && unreg) { unreg(); unreg = null; }
    };
    const wake = () => {
      moved = true;
      if (unreg) return;
      const r = box.get(performance.now(), true);
      const p = pointerEngine.state;
      if (r && nearRect(r, p.x, p.y, RANGE)) unreg = playLoop.register(tick);
    };
    const offs = [
      pointerEngine.on('move', wake), pointerEngine.on('down', wake), pointerEngine.on('up', wake),
    ];
    return () => {
      offs.forEach((o) => o());
      if (unreg) unreg();
      el.style.transform = '';
    };
  }, [ref, pressOnly, off]);
}
